import { Col, Spinner2 } from 'components';
import { Api } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { SubscriptionPlanPanel } from 'screens/Admin/components';
import { List } from './components';
import { filterPlansByTeam } from './lib/filterPlansByTeam';

const getPlansFromResponse = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.plans)) return res.plans;
  return [];
};

const PlansTab = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [search, setSearch] = useState('');
  const [panelVisible, setPanelVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchPlans();
  }, []);

  async function fetchPlans() {
    setLoading(true);
    const res = await Api.get('/admin/subscriptionPlans/list');
    setPlans(getPlansFromResponse(res));
    setLoading(false);
  }

  function handleCreateNew() {
    setSelectedPlan(null);
    setPanelVisible(true);
  }

  function handleSelectPlan(planId) {
    const plan = plans?.find((p) => p._id === planId);
    if (plan) {
      setSelectedPlan(plan);
      setPanelVisible(true);
    }
  }

  function handleClosePanel() {
    setPanelVisible(false);
    setSelectedPlan(null);
  }

  async function handleSyncAll() {
    setSyncing(true);
    const res = await Api.post('/admin/subscriptionPlans/sync', {});
    setSyncing(false);

    if (res) {
      /*
        The counts live at `res.data`, and the server has already written the sentence.

        This read `res.synced` and `res.failed`, which sit one level down — so an admin who synced
        forty plans was told "Synced 0 plans", and a run with failures never mentioned them. The
        sync itself worked the whole time; only the report of it was wrong, which is the sort of
        thing that gets believed.

        Preferring `res.msg` over recomposing the sentence: `sync.ts` already distinguishes a clean
        run from a partial one, and two copies of that wording would eventually disagree about
        which is which. The fallback covers a response that carries counts but no message.
      */
      const synced = res.data?.synced ?? 0;
      const failed = res.data?.failed ?? 0;

      dispatch({
        type: 'HANDLE_RES',
        payload: {
          res: res.msg || `Synced ${synced} plans${failed ? `, ${failed} failed` : ''}`
        }
      });
      fetchPlans();
    }
  }

  async function handleSyncPlan(planId) {
    setSyncingId(planId);
    const res = await Api.post(`/admin/subscriptionPlans/sync/${planId}`, {});
    setSyncingId(null);
    if (res && !res.err) {
      dispatch({ type: 'HANDLE_RES', payload: { res: res.msg || t('subscription.admin.syncPlanDone') } });
    }
    fetchPlans();
  }

  const visiblePlans = filterPlansByTeam(plans, search);
  const isSearching = search.trim().length > 0;
  const isEmpty = !loading && plans.length === 0;
  const noMatches = !loading && plans.length > 0 && visiblePlans.length === 0;

  const syncedCount = visiblePlans.filter((p) => p.syncStatus?.stripe === 'synced').length;
  const pendingCount = visiblePlans.filter((p) => !p.syncStatus || p.syncStatus?.stripe === 'pending').length;
  const errorCount = visiblePlans.filter((p) => p.syncStatus?.stripe === 'error').length;

  return (
    <>
        {/* Filters + Stats Bar */}
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-[#e5e7eb] bg-[#FAFBFC]">
          <div className="relative w-full max-w-[320px]">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#97A3B5] pointer-events-none"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
            >
              <circle cx="7" cy="7" r="5" />
              <path d="M11 11l3.5 3.5" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('subscription.admin.search.placeholder')}
              aria-label={t('subscription.admin.search.placeholder')}
              className="w-full border border-[#B0B8C4] rounded-md pl-9 pr-8 py-[7px] text-[13px] text-[#353E4A] placeholder-[#97A3B5] bg-[#F9FAFB] focus:bg-white focus:outline-none focus:border-[#97A3B5] focus:ring-1 focus:ring-[#B5BDCA] transition-colors [&::-webkit-search-cancel-button]:hidden"
            />
            {isSearching && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label={t('subscription.admin.search.clear')}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded text-[#7989A0] hover:bg-[#F0F2F4] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#97A3B5]"
              >
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>

          {!loading && (
            <div className="flex items-center gap-4 ml-auto">
              <div className="flex items-center gap-3 text-[12px] text-[#7989A0]">
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#27E3A3]" />
                  {syncedCount} {t('subscription.admin.stats.synced')}
                </span>
                {pendingCount > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#eab308]" />
                    {pendingCount} {t('subscription.admin.stats.pending')}
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FA3F58]" />
                    {errorCount} {t('subscription.admin.stats.error')}
                  </span>
                )}
              </div>
              <span className="text-[11px] text-[#B5BDCA]">|</span>
              <span className="text-[12px] font-medium text-[#5F6F86]">
                {visiblePlans.length} {visiblePlans.length !== 1 ? t('subscription.admin.stats.plan_plural') : t('subscription.admin.stats.plan')}
              </span>
            </div>
          )}

          <div className={`flex items-center gap-2.5 ${loading ? 'ml-auto' : 'ml-4'}`}>
                  <button
                    onClick={handleSyncAll}
                    disabled={syncing}
                    className="group flex items-center gap-2 px-4 py-[7px] text-[13px] font-medium text-[#353E4A] bg-white border border-[#D2D8DF] rounded-md hover:border-[#B5BDCA] hover:bg-[#F0F2F4] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : 'group-hover:rotate-45 transition-transform'}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M14 8A6 6 0 1 1 8 2" strokeLinecap="round" />
                      <path d="M8 2L10.5 0M8 2L10.5 4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {syncing ? t('subscription.admin.syncing') : t('subscription.admin.syncAll')}
                  </button>
                  <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-1.5 px-4 py-[7px] text-[13px] font-semibold text-white bg-[#20252D] rounded-md hover:bg-[#353E4A] active:scale-[0.98] transition-all"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M7 1v12M1 7h12" strokeLinecap="round" />
                    </svg>
                    {t('subscription.admin.createPlan')}
                  </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <Col wid="100%" marg="80px 0" centerAll noFlex>
            <Spinner2 />
          </Col>
        )}

        {/* Empty State */}
        {isEmpty && (
          <Col wid="100%" centerAll noFlex marg="80px 0 0" pad="0 24px">
            <div className="flex flex-col items-center text-center max-w-[320px]">
              <div className="w-16 h-16 rounded-2xl bg-[#F0F2F4] flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-[#B5BDCA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <path d="M3 9h18M9 3v18" />
                </svg>
              </div>
              <p className="text-[15px] font-semibold text-[#353E4A] mb-1.5">{t('subscription.admin.empty.title')}</p>
              <p className="text-[13px] text-[#97A3B5] mb-5 leading-relaxed">
                {t('subscription.admin.empty.message')}
              </p>
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-1.5 px-5 py-2.5 text-[13px] font-semibold text-white bg-[#20252D] rounded-md hover:bg-[#353E4A] active:scale-[0.98] transition-all"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 1v12M1 7h12" strokeLinecap="round" />
                </svg>
                {t('subscription.admin.createPlan')}
              </button>
            </div>
          </Col>
        )}

        {/* No team matches the search */}
        {noMatches && (
          <Col wid="100%" centerAll noFlex marg="80px 0 0" pad="0 24px">
            <div className="flex flex-col items-center text-center max-w-[360px]">
              <p className="text-[15px] font-semibold text-[#353E4A] mb-1.5">
                {t('subscription.admin.search.noResultsTitle', { query: search.trim() })}
              </p>
              <p className="text-[13px] text-[#97A3B5] mb-5 leading-relaxed">
                {t('subscription.admin.search.noResultsMessage')}
              </p>
              <button
                type="button"
                onClick={() => setSearch('')}
                className="px-4 py-[7px] text-[13px] font-medium text-[#353E4A] bg-white border border-[#D2D8DF] rounded-md hover:border-[#B5BDCA] hover:bg-[#F0F2F4] transition-colors"
              >
                {t('subscription.admin.search.clear')}
              </button>
            </div>
          </Col>
        )}

        {/* Table */}
        {!loading && visiblePlans.length > 0 && (
          <List
            data={visiblePlans}
            dataId={selectedPlan?._id}
            onClick={handleSelectPlan}
            onSync={handleSyncPlan}
            syncingId={syncingId}
            loading={false}
          />
        )}

      <SubscriptionPlanPanel
        visible={panelVisible}
        closeModal={handleClosePanel}
        plan={selectedPlan}
        onSaved={fetchPlans}
      />
    </>
  );
};

export default PlansTab;
